import React, { useMemo, useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  ArrowLeftIcon,
  ChevronLeftIcon,
  EllipsisHorizontalIcon,
  ExclamationTriangleIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  PencilSquareIcon,
  Cog8ToothIcon,
} from '@heroicons/react/24/outline';
import { LoaderSpinner } from '../LoaderSpinner';
import { arrayHasItems } from '../../helpers/common';
import FilterDropdown from '../FilterDropdown';
import { toast } from 'react-toastify';
import { CHILDREN_TAB_REPORTS, isAdminRole } from '../../consts/management/users/users';
import { useIsMobile } from '../../helpers/useIsMobile';
import { ReportEmailSettingsSidebar } from './ReportEmailSettingsSidebar';
import { SCHEDULABLE_REPORTS } from '../../consts/reportScheduling';
import { useReportScheduling } from '../../hooks/useReportScheduling';

const MOBILE_TOP_NAV_HEIGHT = 56;

const ReportTemplate = ({
  marginFromSidebar,
  title,
  reportId = null,
  access,
  description,
  breadcrumbs = [],
  onBack,
  children,
  totalByPage = null,
  isLoading = false,
  error = null,
  filters = null,
  filterNotice = null,
  exportButton = null,
  data = [],
  columns = [],
  filtersConfig = [],
  pagination = null,
  emptyMessage = "No data available",
  headerContent = null,
  onSort = null, // Callback for sorting - receives { field, direction }
  sortField = null, // Current sort field
  sortDirection = null, // Current sort direction ('asc' or 'desc')
  enableSorting = true, // Global toggle for sorting functionality
  isFirstColumnSticky = true,
  groupedHeaders = null, // Array of { label: string, colSpan: number } for grouped header row
  columnSummaryRow = null, // { [columnKey]: ReactNode } rendered as a second header row below column labels
  cronType = null, // Cron type for the report
  renderChildrenOnly = false,
  disableScheduling = false,
  schedulingPreviewParams = {},
}) => {
  const { currentUser } = useSelector((state) => state.auth);
  const scheduling = useReportScheduling(
    !disableScheduling && reportId && SCHEDULABLE_REPORTS[reportId] ? reportId : null,
    schedulingPreviewParams,
  );
  const isMobile = useIsMobile();
  const stickyTopOffset = isMobile ? MOBILE_TOP_NAV_HEIGHT : 0;
  const hasExplicitColumnWidths = columns.some((column) => column?.width != null);
  const getColumnWidthStyle = (column) => {
    if (column?.width == null) return {};
    return {
      width: typeof column.width === 'number' ? `${column.width}px` : column.width,
    };
  };

  const [remoteTitle, setRemoteTitle] = useState(null);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [draftTitle, setDraftTitle] = useState('');
  const [isSavingTitle, setIsSavingTitle] = useState(false);
  const [titleIsLoading, setTitleIsLoading] = useState(false);

  const reportsAccess = useMemo(() => {
    const userPermissions = currentUser?.permissions || [];
    // Permissions shape: [{ [parentTabId]: [{ [childTabId]: "edit"|"view" }, ...] }, ...]
    let accessLevel = null;
    (userPermissions || []).forEach((permissionGroup) => {
      const [, children] = Object.entries(permissionGroup || {})[0] || [];
      (children || []).forEach((childPermission) => {
        const [childKey, childAccess] = Object.entries(childPermission || {})[0] || [];
        if (childKey === CHILDREN_TAB_REPORTS && (childAccess === 'edit' || childAccess === 'view')) {
          // Prefer 'edit' over 'view'
          if (!accessLevel || childAccess === 'edit') accessLevel = childAccess;
        }
      });
    });
    return accessLevel;
  }, [currentUser]);

  const canEditReportTitle = useMemo(() => {
    if (!reportId) return false;
    // Explicit report access controls ordinary users; admins retain title-edit access.
    const effectiveAccess = typeof access === 'string' ? access : reportsAccess;
    return effectiveAccess === 'edit' || isAdminRole(currentUser?.role);
  }, [access, currentUser?.role, reportsAccess, reportId]);

  const displayTitle = useMemo(() => {
    const fallback =
      (title && typeof title === 'string' ? title : '') ||
      (Array.isArray(breadcrumbs) && breadcrumbs.length > 0 ? breadcrumbs[breadcrumbs.length - 1]?.label : '') ||
      '';
    return (remoteTitle && typeof remoteTitle === 'string' ? remoteTitle : fallback) || '';
  }, [remoteTitle, title, breadcrumbs]);

  const effectiveBreadcrumbs = useMemo(() => {
    if (!Array.isArray(breadcrumbs) || breadcrumbs.length === 0) return [];
    if (!reportId) return breadcrumbs;
    const lastIndex = breadcrumbs.length - 1;
    return breadcrumbs.map((crumb, idx) => {
      if (idx !== lastIndex) return crumb;
      return { ...crumb, label: displayTitle || crumb?.label };
    });
  }, [breadcrumbs, reportId, displayTitle]);

  useEffect(() => {
    let isMounted = true;
    async function loadTitle() {
      try {
        if (!reportId) return;
        setTitleIsLoading(true);
        const res = await fetch('/api/v1/metadata/reports-names', {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        if (!res.ok) return;
        const json = await res.json().catch(() => ({}));
        const list = Array.isArray(json?.reports) ? json.reports : [];
        const found = list.find((x) => x && x.id === reportId);
        if (isMounted) {
          setRemoteTitle(found?.name || null);
        }
      } catch (e) {
        console.error(e);
      } finally {
        setTitleIsLoading(false);
      }
    }
    loadTitle();
    return () => {
      isMounted = false;
    };
  }, [reportId]);

  const beginEditTitle = () => {
    setDraftTitle(displayTitle || '');
    setIsEditingTitle(true);
  };

  const cancelEditTitle = () => {
    setIsEditingTitle(false);
    setDraftTitle('');
  };

  const saveTitle = async () => {
    try {
      if (!reportId) return;
      const next = String(draftTitle || '').trim();
      if (!next) return;
      setIsSavingTitle(true);
      setTitleIsLoading(true);
      const body = { id: reportId, name: next };
      if (cronType) {
        body.cron_type = cronType;
      }
      const res = await fetch('/api/v1/metadata/reports-names', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to update report name');
      }
      setRemoteTitle(next);
      setIsEditingTitle(false);
      setDraftTitle('');
      toast.success('Report name updated successfully', { position: 'bottom-right' });
      try {
        window.dispatchEvent(new CustomEvent('reports-names-updated', { detail: { id: reportId, name: next } }));
      } catch (err) {
        console.error(err);
      }
    } catch (e) {
      toast.error('Failed to update report name', { position: 'bottom-right' });
      console.error(e);
    } finally {
      setIsSavingTitle(false);
      setTitleIsLoading(false);
    }
  };

  // Internal sorting state (used when onSort callback is not provided)
  const [internalSortField, setInternalSortField] = useState(null);
  const [internalSortDirection, setInternalSortDirection] = useState(null);

  // Use external or internal sorting state
  const currentSortField = sortField !== null ? sortField : internalSortField;
  const currentSortDirection = sortDirection !== null ? sortDirection : internalSortDirection;

  // Handle column header click for sorting
  const handleColumnSort = (column) => {
    if (!enableSorting || column.sortable === false) return;

    const field = column.key;
    let direction = 'asc';

    // Toggle direction if clicking the same field
    if (currentSortField === field) {
      direction = currentSortDirection === 'asc' ? 'desc' : 'asc';
    }

    // If parent provides onSort callback, use it (server-side sorting)
    if (onSort) {
      onSort({ field, direction });
    } else {
      // Otherwise use internal state (client-side sorting)
      setInternalSortField(field);
      setInternalSortDirection(direction);
    }
  };

  // Check if a column is sortable
  const isColumnSortable = (column) => {
    return enableSorting && column.sortable !== false;
  };

  // -----------------------------
  // Sticky header logic
  // -----------------------------
  const headerRef = useRef(null);
  const observeRef = useRef(null);
  const [addShadow, setAddShadow] = useState(false);
  const [paddingFromHeader, setPaddingFromHeader] = useState(0);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // -----------------------------
  // Floating table header (under top sticky block)
  // -----------------------------
  const tableContainerRef = useRef(null);
  const theadRef = useRef(null);
  const tbodyRef = useRef(null);
  const [isFloatingHeadVisible, setIsFloatingHeadVisible] = useState(false);
  const [floatingHeadColWidths, setFloatingHeadColWidths] = useState([]);
  const [tableContainerMetrics, setTableContainerMetrics] = useState({ left: 0, width: 0 });
  const [horizontalScrollLeft, setHorizontalScrollLeft] = useState(0);

  useEffect(() => {
    const updateHeaderMetrics = () => {
      if (headerRef.current) {
        setPaddingFromHeader(headerRef.current.offsetHeight || 0);
      }
    };

    updateHeaderMetrics();

    window.addEventListener('resize', updateHeaderMetrics);

    let headerResizeObserver;
    if (headerRef.current) {
      headerResizeObserver = new ResizeObserver(updateHeaderMetrics);
      headerResizeObserver.observe(headerRef.current);
    }

    const currentObserve = observeRef.current;
    let observer;
    if (currentObserve) {
      observer = new IntersectionObserver(
        ([entry]) => {
          setAddShadow(!entry.isIntersecting);
        },
        {
          root: null,
          threshold: 0,
          rootMargin: '0px 0px -100% 0px',
        }
      );
      observer.observe(currentObserve);
    }

    return () => {
      window.removeEventListener('resize', updateHeaderMetrics);
      if (headerResizeObserver) {
        headerResizeObserver.disconnect();
      }
      if (observer && currentObserve) {
        observer.unobserve(currentObserve);
      }
    };
  }, []);

  // Measure table container metrics and column widths
  useEffect(() => {
    const measure = () => {
      if (tableContainerRef.current) {
        const rect = tableContainerRef.current.getBoundingClientRect();
        setTableContainerMetrics({ left: rect.left, width: rect.width });
      }
      // Prefer measuring from THEAD cells so header/body stay identical
      let measured = [];
      if (theadRef.current) {
        const ths = Array.from(theadRef.current.querySelectorAll('th'));
        measured = ths.map((th) => th.getBoundingClientRect().width);
      }
      // Fallback to first tbody row if thead hasn't laid out yet
      if (!measured.length && tbodyRef.current) {
        const firstRow = tbodyRef.current.querySelector('tr');
        if (firstRow) {
          const cells = Array.from(firstRow.querySelectorAll('th,td'));
          measured = cells.map((cell) => cell.getBoundingClientRect().width);
        }
      }
      if (measured.length) setFloatingHeadColWidths(measured);
    };

    measure();
    window.addEventListener('resize', measure);
    const resizeObserver = new ResizeObserver(measure);
    if (tableContainerRef.current) {
      resizeObserver.observe(tableContainerRef.current);
    }
    if (theadRef.current) {
      resizeObserver.observe(theadRef.current);
    }
    if (tbodyRef.current) {
      resizeObserver.observe(tbodyRef.current);
    }

    return () => {
      window.removeEventListener('resize', measure);
      resizeObserver.disconnect();
    };
  }, [columns, data]);

  // Sync horizontal scroll for floating header alignment
  useEffect(() => {
    const el = tableContainerRef.current;
    if (!el) return;
    const onScroll = () => setHorizontalScrollLeft(el.scrollLeft || 0);
    // initialize position in case we mount with an existing scroll
    onScroll();
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [columns.length, data.length, isFloatingHeadVisible]);

  // Toggle floating header visibility based on thead position relative to viewport
  // This works regardless of horizontal overflow presence
  useEffect(() => {
    const updateVisibility = () => {
      if (!theadRef.current) return;
      const rect = theadRef.current.getBoundingClientRect();
      // Show floating header when table header top has scrolled past the sticky block
      setIsFloatingHeadVisible(rect.bottom <= paddingFromHeader + stickyTopOffset);
    };
    updateVisibility();
    window.addEventListener('scroll', updateVisibility, { passive: true });
    window.addEventListener('resize', updateVisibility);
    return () => {
      window.removeEventListener('scroll', updateVisibility);
      window.removeEventListener('resize', updateVisibility);
    };
  }, [paddingFromHeader, stickyTopOffset, columns.length, data.length]);

  // When floating header toggles, sync initial horizontal offset
  useEffect(() => {
    if (!isFloatingHeadVisible) return;
    if (tableContainerRef.current) {
      setHorizontalScrollLeft(tableContainerRef.current.scrollLeft || 0);
    }
  }, [isFloatingHeadVisible]);

  // Get sort icon for a column
  const getSortIcon = (column) => {
    if (!isColumnSortable(column)) return null;

    const field = column.key;
    const isActive = currentSortField === field;

    if (!isActive) {
      return (
        <div className="ml-1 flex flex-col">
          <ChevronUpIcon className="h-3 w-3 text-gray-300" />
          <ChevronDownIcon className="h-3 w-3 text-gray-300 -mt-1" />
        </div>
      );
    }

    return currentSortDirection === 'asc' ? (
      <ChevronUpIcon className="ml-1 h-4 w-4 text-gray-600" />
    ) : (
      <ChevronDownIcon className="ml-1 h-4 w-4 text-gray-600" />
    );
  };

  const getColumnAlign = (column) => {
    // Explicit override supported
    if (column?.align === 'right') return 'right';
    if (column?.align === 'center') return 'center';
    if (column?.align === 'left') return 'left';

    const type = String(column?.type || '').toLowerCase();
    // Common numeric-like types from report metadata
    if (['number', 'currency', 'percent', 'percentage', 'integer', 'float', 'decimal'].includes(type)) {
      return 'right';
    }
    return 'left';
  };

  const getAlignClass = (column) => {
    const align = getColumnAlign(column);
    if (align === 'right') return 'text-right';
    if (align === 'center') return 'text-center';
    return 'text-left';
  };

  const getHeaderJustifyClass = (column) => {
    const align = getColumnAlign(column);
    if (align === 'right') return 'justify-end';
    if (align === 'center') return 'justify-center';
    return 'justify-start';
  };

  // Determine if the first column should be sticky
  // const isFirstColumnSticky = Array.isArray(columns) && columns.length > 0 && columns[0]?.sticky === true;

  const scheduledExportButton = scheduling.isSchedulable ? (
    <div className="flex items-center space-x-2">
      {exportButton}
      <div>
        <Cog8ToothIcon
          className="h-5 w-5 text-primary cursor-pointer"
          onClick={scheduling.open}
          aria-label="Report settings"
        />
      </div>
    </div>
  ) : exportButton;

  return (
    <>
    <div className="min-h-screen bg-gray-100">
      {/* Header Section - Breadcrumbs, back button, title, filters, etc. */}
      <div
        ref={headerRef}
        className={`flex flex-col sticky z-10 ${addShadow ? 'shadow-md' : ''}`}
        style={{ top: `${stickyTopOffset}px` }}
      >
        <div className="px-4 sm:px-6 lg:px-8 bg-white">
          <div className="py-4">
            {/* Breadcrumb and Back Button */}
            <div className="flex items-start justify-between">
              {/* Mobile back icon button */}
              {onBack && (
                <button
                  onClick={onBack}
                  aria-label="Back"
                  className="md:hidden mr-3 inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white p-2 text-gray-700 shadow-sm hover:bg-gray-50"
                >
                  <ChevronLeftIcon className="h-5 w-5" />
                </button>
              )}
              <div className="flex-1 min-w-0">
                {effectiveBreadcrumbs.length > 0 && (
                  <nav className="flex items-center" aria-label="Breadcrumb">
                    <ol className="flex items-center space-x-4">
                      {effectiveBreadcrumbs.map((crumb, index) => (
                        <li key={index} className={index === 0 ? '' : 'hidden md:block'}>
                          {index === 0 ? (
                            <div className="flex items-center">
                              {crumb.icon}
                              <span className="ml-2 text-sm font-medium text-gray-500">{crumb.label}</span>
                            </div>
                          ) : (
                            <div className="flex items-center">
                              <svg className="h-5 w-5 text-gray-300" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                              </svg>
                              <span className="ml-4 text-sm font-medium text-gray-500">{crumb.label}</span>
                            </div>
                          )}
                        </li>
                      ))}
                    </ol>
                    {onBack && (
                      <button
                        onClick={onBack}
                        className="ml-4 hidden items-center gap-1 text-sm font-medium text-gray-500 transition-colors hover:text-gray-900 md:inline-flex"
                      >
                        <ArrowLeftIcon className="h-4 w-4" />
                        Back
                      </button>
                    )}
                  </nav>
                )}
              </div>
              {/* Mobile overflow menu */}
              {reportId && canEditReportTitle && (
                <div className="relative md:hidden ml-3">
                  <button
                    onClick={() => setIsMobileMenuOpen((prev) => !prev)}
                    aria-label="More options"
                    className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white p-2 text-gray-700 shadow-sm hover:bg-gray-50"
                  >
                    <EllipsisHorizontalIcon className="h-5 w-5" />
                  </button>
                  {isMobileMenuOpen && (
                    <>
                      <div className="fixed inset-0 z-20" onClick={() => setIsMobileMenuOpen(false)} />
                      <div className="absolute right-0 mt-2 w-48 rounded-md border border-gray-200 bg-white shadow-lg z-30">
                        <button
                          onClick={() => {
                            setIsMobileMenuOpen(false);
                            beginEditTitle();
                          }}
                          className="flex w-full items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                        >
                          <PencilSquareIcon className="h-4 w-4" />
                          Edit report name
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="px-4 sm:px-6 lg:px-8 py-4 pb-0 bg-gray-100">
          <div className="max-w-[1800px] mx-auto">
            {/* Title, Description, and Actions */}
            <div className="mb-6">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                <div className="mb-4 sm:mb-0 flex-1">
                  {!reportId || !isEditingTitle ? (
                    <div className="flex items-center gap-2">
                      {titleIsLoading ? (
                        <LoaderSpinner colorSpinner="blue" height="20" width="20" border="4" />
                      ) : (
                        <>
                          <h1 className="text-2xl font-bold text-gray-900">{displayTitle}</h1>
                          {reportId && canEditReportTitle && (
                            <button
                              type="button"
                              onClick={beginEditTitle}
                              className="inline-flex items-center justify-center p-1 text-gray-400 transition-colors hover:text-gray-900"
                              title="Edit report title"
                            >
                              <PencilSquareIcon className="h-4 w-4" />
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  ) : (
                    <div className="flex flex-row gap-2">
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={draftTitle}
                          onChange={(e) => setDraftTitle(e.target.value)}
                          className="w-full max-w-[520px] rounded-md border border-gray-300 px-3 py-1.5 text-base font-semibold text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                          placeholder="Report title"
                          autoFocus
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={saveTitle}
                          disabled={isSavingTitle || !String(draftTitle || '').trim()}
                          className="inline-flex items-center rounded-md bg-black px-3 py-2 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-50"
                        >
                          {isSavingTitle ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          type="button"
                          onClick={cancelEditTitle}
                          disabled={isSavingTitle}
                          className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Filters and Export Actions */}
                <div className="flex flex-row flex-wrap items-center gap-2 sm:gap-3">
                  {filters}
                  {scheduledExportButton}
                </div>
              </div>
              {description && (
                <p className="mt-4 text-sm text-gray-700 line-clamp-1 md:line-clamp-2" title={description}>{description}</p>
              )}
              {headerContent && (
                <div className="mt-4">
                  {headerContent}
                </div>
              )}
            </div>
            {/* Error State */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">Error Loading Report</h3>
                    <div className="mt-2 text-sm text-red-700">
                      <p>{error}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {(arrayHasItems(filtersConfig) || totalByPage !== null) && (
              <section className="flex flex-row gap-4 justify-between items-center mb-4">
                {arrayHasItems(filtersConfig) && (
                  <div className="flex flex-wrap items-center gap-4 w-full">
                    {filtersConfig.map((config, index) => {
                      return (
                        <div className={`relative`} key={`${config.key}_${index}`}>
                          <FilterDropdown
                            {...config}
                          />
                        </div>
                      )
                    })}
                    {filterNotice}
                  </div>
                )}
                {totalByPage !== null && (
                  <div>
                      <div className="font-bold text-lg whitespace-nowrap">Total: {totalByPage?.toLocaleString(undefined, { style: 'currency', currency: 'USD' }) || '$0.00'}</div>
                  </div>
                )}
              </section>
            )}
          </div>
        </div>
      </div>

      {/* Sentinel div for IntersectionObserver */}
      <div ref={observeRef} style={{ height: 1 }} />

      {/* Main Content */}
      <div className="px-4 sm:px-6 lg:px-8 py-8">
        <div className="max-w-[1800px] mx-auto">
          {/* Loading State */}
          {isLoading ? (
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-12 text-center">
                <LoaderSpinner />
                <p className="mt-4 text-sm text-gray-500">Loading report data...</p>
              </div>
            </div>
          ) : (
            <>
              {renderChildrenOnly ? (
                <>
                  {children}
                  {pagination && (
                    <div className="bg-white px-4 py-3 border-t border-gray-200">
                      {pagination}
                    </div>
                  )}
                </>
              ) : (
                /* Data Display Section */
                <div className="bg-white rounded-lg shadow relative">
                {data.length === 0 ? (
                  /* Empty State */
                  <div className="px-6 py-12 text-center">
                    <div className="mx-auto h-16 w-16 bg-gray-100 rounded-full flex items-center justify-center">
                      <svg className="h-8 w-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                    </div>
                    <h3 className="mt-4 text-lg font-medium text-gray-900">No Data</h3>
                    <p className="mt-2 text-sm text-gray-500">{emptyMessage}</p>
                  </div>
                ) : (
                  <>
                    {/* Table Display */}
                    <div className="overflow-hidden">
                      <div
                        ref={tableContainerRef}
                        className={`overflow-x-auto ${isFirstColumnSticky ? 'kl-sticky-first-col' : ''}`}
                      >
                        {isFirstColumnSticky && (
                          <style>
                            {`
                              .kl-sticky-first-col table thead th:first-child,
                              .kl-sticky-first-col table tbody td:first-child {
                                position: sticky;
                                left: 0;
                                z-index: 1;
                                background-color: #ffffff;
                              }

                              .kl-sticky-first-col table thead th:first-child {
                                z-index: 2;
                                background-color: #F9FAFB;
                              }
                              .kl-sticky-first-col table thead th:first-child::after,
                              .kl-sticky-first-col table tbody td:first-child::after {
                                content: "";
                                position: absolute;
                                top: 0;
                                right: 0;
                                bottom: 0;
                                width: 1px;
                                background-color: #e5e7eb; /* Tailwind gray-200 */
                              }
                            `}
                          </style>
                        )}
                        <table className="min-w-full divide-y divide-gray-200">
                          {hasExplicitColumnWidths && (
                            <colgroup>
                              {columns.map((column) => (
                                <col key={`column-${column.key}`} style={getColumnWidthStyle(column)} />
                              ))}
                              <col key="column-filler" />
                            </colgroup>
                          )}
                          {columns.length > 0 && (
                            <thead
                              ref={theadRef}
                              className="bg-gray-50"
                              style={{ visibility: isFloatingHeadVisible ? 'hidden' : 'visible' }}
                            >
                              {groupedHeaders && groupedHeaders.length > 0 && (
                                <tr className="border-b-2 border-gray-200">
                                  {groupedHeaders.map((group, index) => (
                                    <th
                                      key={index}
                                      colSpan={group.colSpan}
                                      className={`px-6 py-3 text-xs whitespace-nowrap font-medium text-gray-500 uppercase tracking-wider ${group.colSpan > 1 ? 'text-center' : 'text-right'}`}
                                    >
                                      {group.label}
                                    </th>
                                  ))}
                                </tr>
                              )}
                              <tr>
                                {columns.map((column, index) => (
                                  <th
                                    key={index}
                                    className={`px-6 py-3 ${getAlignClass(column)} text-xs whitespace-nowrap font-medium text-gray-500 uppercase tracking-wider ${
                                      isColumnSortable(column) 
                                        ? 'cursor-pointer hover:bg-gray-100 select-none' 
                                        : ''
                                    }`}
                                    onClick={() => handleColumnSort(column)}
                                    role={isColumnSortable(column) ? "button" : undefined}
                                    tabIndex={isColumnSortable(column) ? 0 : undefined}
                                    onKeyDown={(e) => {
                                      if (isColumnSortable(column) && (e.key === 'Enter' || e.key === ' ')) {
                                        e.preventDefault();
                                        handleColumnSort(column);
                                      }
                                    }}
                                    aria-sort={
                                      currentSortField === column.key 
                                        ? currentSortDirection === 'asc' ? 'ascending' : 'descending'
                                        : isColumnSortable(column) ? 'none' : undefined
                                    }
                                  >
                                    <div className={`flex items-center ${getHeaderJustifyClass(column)}`}>
                                      {column.label}
                                      {getSortIcon(column)}
                                    </div>
                                  </th>
                                ))}
                              </tr>
                              {columnSummaryRow && (
                                <tr className="bg-gray-50 border-b border-gray-200">
                                  {columns.map((column, index) => (
                                    <th
                                      key={`summary-${column.key || index}`}
                                      className={`px-6 py-1 text-xs font-semibold whitespace-nowrap ${getAlignClass(column)}`}
                                    >
                                      {columnSummaryRow[column.key] ?? null}
                                    </th>
                                  ))}
                                </tr>
                              )}
                            </thead>
                          )}
                          <tbody ref={tbodyRef} className="bg-white divide-y divide-gray-200">
                            {children}
                          </tbody>
                        </table>
                      </div>

                      {/* Floating header rendered under the sticky top block */}
                      {columns.length > 0 && isFloatingHeadVisible && floatingHeadColWidths.length > 0 && (
                        <div
                          className="bg-gray-50 border-b border-gray-200"
                          style={{
                            position: 'fixed',
                            top: `${paddingFromHeader + stickyTopOffset}px`,
                            left: `${tableContainerMetrics.left}px`,
                            width: `${tableContainerMetrics.width}px`,
                            zIndex: 9, // under the top header (z-10)
                            overflow: 'hidden',
                            pointerEvents: 'auto', // allow this header to forward scroll gestures
                            cursor: 'grab',
                          }}
                          onWheel={(e) => {
                            if (!tableContainerRef.current) return;
                            const delta = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
                            tableContainerRef.current.scrollLeft += delta;
                            e.preventDefault();
                          }}
                          onPointerDown={(e) => {
                            if (!tableContainerRef.current) return;
                            e.currentTarget.setPointerCapture?.(e.pointerId);
                            e.currentTarget.dataset.dragStartX = String(e.clientX);
                            e.currentTarget.dataset.dragStartScrollLeft = String(tableContainerRef.current.scrollLeft);
                            e.currentTarget.dataset.dragging = '1';
                          }}
                          onPointerMove={(e) => {
                            const target = e.currentTarget;
                            if (target.dataset.dragging !== '1' || !tableContainerRef.current) return;
                            const startX = Number(target.dataset.dragStartX || 0);
                            const startScroll = Number(target.dataset.dragStartScrollLeft || 0);
                            const dx = e.clientX - startX;
                            tableContainerRef.current.scrollLeft = startScroll - dx;
                          }}
                          onPointerUp={(e) => {
                            const target = e.currentTarget;
                            target.dataset.dragging = '0';
                            target.releasePointerCapture?.(e.pointerId);
                          }}
                        >
                          <div style={{ position: 'relative' }}>
                            {/* First column pinned separately so it doesn't shift with translate */}
                            {isFirstColumnSticky && columns.length > 0 && (
                              <div
                                style={{
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  width: `${floatingHeadColWidths[0] || 0}px`,
                                  backgroundColor: '#F9FAFB',
                                  borderRight: '1px solid #e5e7eb',
                                  zIndex: 2,
                                  overflow: 'hidden',
                                }}
                              >
                                <table className="min-w-full table-fixed" style={{ tableLayout: 'fixed' }}>
                                  {hasExplicitColumnWidths && (
                                    <colgroup>
                                      <col style={getColumnWidthStyle(columns[0])} />
                                    </colgroup>
                                  )}
                                  <thead className="bg-gray-50">
                                    <tr>
                                      <th
                                        key="floating-0"
                                        style={{
                                          ...getColumnWidthStyle(columns[0]),
                                          minWidth: floatingHeadColWidths[0] || 'auto',
                                        }}
                                        className={`report-sticky-header px-6 py-3 ${getAlignClass(columns[0])} text-xs whitespace-nowrap font-medium text-gray-500 uppercase tracking-wider ${
                                          isColumnSortable(columns[0]) ? 'cursor-pointer hover:bg-gray-100 select-none' : ''
                                        }`}
                                        onClick={() => handleColumnSort(columns[0])}
                                        role={isColumnSortable(columns[0]) ? 'button' : undefined}
                                        tabIndex={isColumnSortable(columns[0]) ? 0 : undefined}
                                        onKeyDown={(e) => {
                                          if (isColumnSortable(columns[0]) && (e.key === 'Enter' || e.key === ' ')) {
                                            e.preventDefault();
                                            handleColumnSort(columns[0]);
                                          }
                                        }}
                                        aria-sort={
                                          currentSortField === columns[0].key
                                            ? currentSortDirection === 'asc'
                                              ? 'ascending'
                                              : 'descending'
                                            : isColumnSortable(columns[0])
                                            ? 'none'
                                            : undefined
                                        }
                                      >
                                        <div className={`flex items-center ${getHeaderJustifyClass(columns[0])}`}>
                                          {columns[0].label}
                                          {getSortIcon(columns[0])}
                                        </div>
                                      </th>
                                    </tr>
                                  </thead>
                                </table>
                              </div>
                            )}

                            {/* Remaining columns that track horizontal scroll */}
                            <div
                              style={{
                                marginLeft: isFirstColumnSticky ? `${floatingHeadColWidths[0] || 0}px` : 0,
                                transform: `translateX(-${horizontalScrollLeft}px)`,
                              }}
                            >
                              <table className="min-w-full table-fixed" style={{ tableLayout: 'fixed' }}>
                                {hasExplicitColumnWidths && (
                                  <colgroup>
                                    {columns.map((column, index) => {
                                      if (isFirstColumnSticky && index === 0) return null;
                                      return (
                                        <col
                                          key={`floating-column-${column.key || index}`}
                                          style={getColumnWidthStyle(column)}
                                        />
                                      );
                                    })}
                                    <col key="floating-column-filler" />
                                  </colgroup>
                                )}
                                <thead className="bg-gray-50">
                                  <tr>
                                    {columns.map((column, index) => {
                                      if (isFirstColumnSticky && index === 0) return null;
                                      return (
                                        <th
                                          key={`floating-${index}`}
                                          style={{
                                            ...getColumnWidthStyle(column),
                                            minWidth: floatingHeadColWidths[index] || 'auto',
                                          }}
                                          className={`report-sticky-header px-6 py-3 ${getAlignClass(column)} text-xs whitespace-nowrap font-medium text-gray-500 uppercase tracking-wider ${
                                            isColumnSortable(column) ? 'cursor-pointer hover:bg-gray-100 select-none' : ''
                                          }`}
                                          onClick={() => handleColumnSort(column)}
                                          role={isColumnSortable(column) ? 'button' : undefined}
                                          tabIndex={isColumnSortable(column) ? 0 : undefined}
                                          onKeyDown={(e) => {
                                            if (isColumnSortable(column) && (e.key === 'Enter' || e.key === ' ')) {
                                              e.preventDefault();
                                              handleColumnSort(column);
                                            }
                                          }}
                                          aria-sort={
                                            currentSortField === column.key
                                              ? currentSortDirection === 'asc'
                                                ? 'ascending'
                                                : 'descending'
                                              : isColumnSortable(column)
                                              ? 'none'
                                              : undefined
                                          }
                                        >
                                          <div className={`flex items-center ${getHeaderJustifyClass(column)}`}>
                                            {column.label}
                                            {getSortIcon(column)}
                                          </div>
                                        </th>
                                      );
                                    })}
                                  </tr>
                                </thead>
                              </table>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Pagination */}
                    {pagination && (
                      <div className="bg-white px-4 py-3 border-t border-gray-200">
                        {pagination}
                      </div>
                    )}
                  </>
                )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
    {scheduling.isSchedulable && (
      <ReportEmailSettingsSidebar
        reportType={reportId}
        isOpen={scheduling.isOpen}
        marginFromSidebar={marginFromSidebar}
        onClose={scheduling.close}
        isLoading={scheduling.isLoadingEmailSettings}
        onSuccessSubmit={scheduling.handleSuccessSubmit}
        sendPreviewEmail={scheduling.sendPreviewEmail}
        sendPreviewSlack={scheduling.sendPreviewSlack}
        defaultSettings={scheduling.emailSettings || {}}
        sendHistory={scheduling.sendHistory}
        isLoadingSendHistory={scheduling.isLoadingSendHistory}
        downloadFilenamePrefix={SCHEDULABLE_REPORTS[reportId].filenamePrefix}
        sheetSyncHandlerParams={schedulingPreviewParams}
      />
    )}
    </>
  );
};

export default ReportTemplate; 